import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, School, Palette, Trash2, Archive, RotateCcw, History, FileDown, Inbox, KeyRound, Copy } from "lucide-react";

const DEMO_ACCOUNTS = [
  { label: "LearnDC Admin", email: "admin@learndc.demo", password: "LearnDC2026!" },
  { label: "LearnDC Provider", email: "provider@learndc.demo", password: "Provider2026!" },
];

const ACTION_STYLES = {
  created: "bg-blue-100 text-blue-700",
  archived: "bg-amber-100 text-amber-700",
  restored: "bg-green-100 text-green-700",
  "permanently deleted": "bg-red-100 text-red-700",
};

export default function ClientsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [clients, setClients] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolOpen, setSchoolOpen] = useState(false);
  const EMPTY_SCHOOL = { mode: "existing", client_id: "", name: "", new_name: "", new_code: "", threshold: 60 };
  const [schoolForm, setSchoolForm] = useState(EMPTY_SCHOOL);
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandForm, setBrandForm] = useState({ client_id: "", name: "", logo_url: "", primary_color: "#0D6E6E", subdomain: "" });
  const [showArchived, setShowArchived] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [activity, setActivity] = useState([]);
  const [showLeads, setShowLeads] = useState(false);
  const [leads, setLeads] = useState([]);
  const [showDemo, setShowDemo] = useState(false);

  const load = useCallback(() => {
    api.get("/clients", { params: isSuper ? { include_archived: true } : {} }).then((r) => setClients(r.data));
    api.get("/schools").then((r) => setSchools(r.data));
    if (isSuper) {
      api.get("/activity-log").then((r) => setActivity(r.data)).catch(() => {});
      api.get("/demo-requests").then((r) => setLeads(r.data)).catch(() => {});
    }
  }, [isSuper]);
  useEffect(() => { load(); }, [load]);

  const activeClients = clients.filter((c) => !c.archived);
  const archivedClients = clients.filter((c) => c.archived);

  const saveSchool = async () => {
    try {
      let cid = schoolForm.client_id;
      if (isSuper && schoolForm.mode === "new") {
        if (!schoolForm.new_name.trim() || !schoolForm.new_code.trim()) return toast.error("Client name and code are required");
        const { data } = await api.post("/clients", {
          name: schoolForm.new_name, code: schoolForm.new_code, return_threshold_minutes: schoolForm.threshold });
        cid = data.id;
        toast.success("Client created");
      } else if (isSuper && !cid) {
        return toast.error("Select a client first");
      }
      if (schoolForm.name.trim()) {
        await api.post("/schools", { name: schoolForm.name, client_id: isSuper ? cid : undefined });
        toast.success("School added");
      } else if (!isSuper || schoolForm.mode === "existing") {
        return toast.error("School name is required");
      }
      setSchoolOpen(false); setSchoolForm(EMPTY_SCHOOL); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const archiveClient = async (c) => {
    if (!window.confirm(`Archive ${c.name}? Its portal will be disabled but all history is kept — you can restore it anytime.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success(`${c.name} archived`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const restoreClient = async (c) => {
    try {
      await api.post(`/clients/${c.id}/restore`);
      toast.success(`${c.name} restored`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const permanentDelete = async (c) => {
    if (!window.confirm(`PERMANENTLY delete ${c.name}? This erases all of its schools, students, users and verification history. This cannot be undone.`)) return;
    try {
      await api.delete(`/clients/${c.id}/permanent`);
      toast.success(`${c.name} permanently deleted`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openBranding = (c) => {
    setBrandForm({ client_id: c.id, name: c.name, logo_url: c.logo_url || "", primary_color: c.primary_color || "#0D6E6E", subdomain: c.subdomain || "" });
    setBrandOpen(true);
  };

  const saveBranding = async () => {
    try {
      await api.put(`/clients/${brandForm.client_id}/branding`, { logo_url: brandForm.logo_url, primary_color: brandForm.primary_color, subdomain: brandForm.subdomain });
      toast.success("Branding updated — refresh to see it applied across the portal");
      setBrandOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportLog = async () => {
    try {
      const res = await api.get("/activity-log/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "activity_log.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Activity log exported");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="clients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1.5">Tenants</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
            {isSuper ? "Client Portals" : "My Organization"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{isSuper ? "All tenant organizations on TAGVerify" : "Schools and settings"}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setSchoolForm(EMPTY_SCHOOL); setSchoolOpen(true); }} data-testid="add-school-button" className="rounded-full bg-primary hover:bg-primary/90">
            <School size={15} className="mr-1.5" /> {isSuper ? "Add School / Client" : "Add School"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {activeClients.map((c) => (
          <div key={c.id} className="rounded-lg border border-slate-200 bg-white shadow-soft card-lift p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`client-card-${c.code}`}>
            <div className="flex items-start justify-between">
              {c.logo_url ? (
                <img src={c.logo_url} alt={c.name} className="h-10 w-10 rounded-lg object-contain" data-testid={`client-logo-${c.code}`} />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-lg text-white" style={{ backgroundColor: c.primary_color || "#0D6E6E" }}>
                  <Building2 size={18} />
                </div>
              )}
              <span className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-xs font-semibold text-slate-600">{c.code}</span>
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-900">{c.name}</h3>
            <div className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200 text-center">
              <div className="bg-white p-2">
                <div className="font-mono text-lg font-semibold text-slate-900">{c.school_count}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Schools</div>
              </div>
              <div className="bg-white p-2">
                <div className="font-mono text-lg font-semibold text-slate-900">{c.student_count}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Students</div>
              </div>
              <div className="bg-white p-2">
                <div className="font-mono text-lg font-semibold text-amber-600">{c.in_service_count}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">In Service</div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span>Return alert threshold: <span className="font-mono font-semibold">{c.return_threshold_minutes} min</span></span>
              <div className="flex items-center gap-1.5">
                <button onClick={() => openBranding(c)} data-testid={`branding-button-${c.code}`}
                  className="flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 font-medium text-slate-600 transition-colors duration-200 hover:bg-slate-100">
                  <Palette size={12} /> Branding
                </button>
                {isSuper && (
                  <button onClick={() => archiveClient(c)} data-testid={`archive-client-${c.code}`}
                    className="flex items-center gap-1 rounded-full border border-amber-200 px-2.5 py-1 font-medium text-amber-700 transition-colors duration-200 hover:bg-amber-50">
                    <Archive size={12} /> Archive
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Schools</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {schools.filter((s) => s.client_id === c.id).map((s) => (
                  <span key={s.id} className="rounded-full border border-slate-200 px-2.5 py-0.5 text-xs text-slate-600">{s.name}</span>
                ))}
                {schools.filter((s) => s.client_id === c.id).length === 0 && <span className="text-xs text-slate-400">No schools yet</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isSuper && archivedClients.length > 0 && (
        <section className="space-y-3">
          <button onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archived-button"
            className="flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors duration-200 hover:text-slate-900">
            <Archive size={15} /> Archived clients ({archivedClients.length}) {showArchived ? "▾" : "▸"}
          </button>
          {showArchived && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {archivedClients.map((c) => (
                <div key={c.id} className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5" data-testid={`archived-card-${c.code}`}>
                  <div className="flex items-start justify-between">
                    <h3 className="font-display text-lg font-semibold tracking-tight text-slate-500">{c.name}</h3>
                    <span className="rounded-full bg-slate-200 px-2.5 py-1 font-mono text-xs font-semibold text-slate-500">{c.code}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    Archived {c.archived_at ? new Date(c.archived_at).toLocaleDateString() : ""} · {c.student_count} students · {c.school_count} schools · history preserved
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => restoreClient(c)} data-testid={`restore-client-${c.code}`}
                      className="flex items-center gap-1 rounded-full border border-green-300 bg-white px-3 py-1.5 text-xs font-medium text-green-700 transition-colors duration-200 hover:bg-green-50">
                      <RotateCcw size={12} /> Restore
                    </button>
                    <button onClick={() => permanentDelete(c)} data-testid={`permanent-delete-${c.code}`}
                      className="flex items-center gap-1 rounded-full border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-600 transition-colors duration-200 hover:bg-red-50">
                      <Trash2 size={12} /> Delete permanently
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {isSuper && (
        <section className="space-y-3">
          <button onClick={() => setShowLeads(!showLeads)} data-testid="toggle-demo-requests-button"
            className="flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors duration-200 hover:text-slate-900">
            <Inbox size={15} /> Demo requests ({leads.length}) {showLeads ? "▾" : "▸"}
          </button>
          {showLeads && (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft" data-testid="demo-requests-list">
              {leads.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-400">No demo requests yet — leads from the home page will appear here</div>
              )}
              {leads.map((l) => (
                <div key={l.id} className="border-b border-slate-100 px-4 py-3 last:border-b-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm">
                      <span className="font-semibold text-slate-900">{l.name}</span>
                      <span className="text-slate-500"> · {l.organization} · </span>
                      <a href={`mailto:${l.email}`} className="font-medium text-primary underline">{l.email}</a>
                    </div>
                    <span className="font-mono text-xs text-slate-400">
                      {new Date(l.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  {l.message && <p className="mt-1 text-sm text-slate-600">{l.message}</p>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {isSuper && (
        <section className="space-y-3">
          <button onClick={() => setShowDemo(!showDemo)} data-testid="toggle-demo-access-button"
            className="flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors duration-200 hover:text-slate-900">
            <KeyRound size={15} /> Demo access {showDemo ? "▾" : "▸"}
          </button>
          {showDemo && (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft" data-testid="demo-access-list">
              {DEMO_ACCOUNTS.map((d) => (
                <div key={d.email} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 last:border-b-0" data-testid={`demo-account-${d.email}`}>
                  <div className="text-sm">
                    <span className="font-semibold text-slate-900">{d.label}</span>
                    <span className="text-slate-500"> · </span>
                    <span className="font-mono text-xs text-slate-600">{d.email}</span>
                    <span className="text-slate-500"> · </span>
                    <span className="font-mono text-xs text-slate-600">{d.password}</span>
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText(`${d.email} / ${d.password}`); toast.success("Credentials copied"); }}
                    data-testid={`copy-demo-${d.email}`}
                    className="flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors duration-200 hover:bg-slate-100">
                    <Copy size={12} /> Copy
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {isSuper && (
        <section className="space-y-3">
          <div className="flex items-center gap-4">
            <button onClick={() => setShowLog(!showLog)} data-testid="toggle-activity-log-button"
              className="flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors duration-200 hover:text-slate-900">
              <History size={15} /> Activity log ({activity.length}) {showLog ? "▾" : "▸"}
            </button>
            <button onClick={exportLog} data-testid="export-activity-log-button"
              className="flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors duration-200 hover:bg-slate-100">
              <FileDown size={12} /> Export CSV
            </button>
          </div>
          {showLog && (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft card-lift" data-testid="activity-log">
              {activity.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-400">No client activity recorded yet</div>
              )}
              {activity.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 last:border-b-0" data-testid={`activity-entry-${a.id}`}>
                  <div className="flex items-center gap-3 text-sm">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ACTION_STYLES[a.action] || "bg-slate-100 text-slate-600"}`}>
                      {a.action}
                    </span>
                    <span className="font-medium text-slate-900">{a.client_name}</span>
                    {a.client_code && <span className="font-mono text-xs text-slate-400">{a.client_code}</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    by <span className="font-medium text-slate-700">{a.actor_name}</span> ({a.actor_email}) ·{" "}
                    <span className="font-mono">{new Date(a.created_at).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <Dialog open={brandOpen} onOpenChange={setBrandOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Portal Branding — {brandForm.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Logo URL</Label>
              <Input value={brandForm.logo_url} onChange={(e) => setBrandForm({ ...brandForm, logo_url: e.target.value })}
                placeholder="https://yourdistrict.edu/logo.png" data-testid="branding-logo-input" />
              <p className="text-xs text-slate-400">Paste a link to the district's logo image (PNG/SVG works best)</p>
            </div>
            <div className="space-y-1.5">
              <Label>Primary color</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={brandForm.primary_color}
                  onChange={(e) => setBrandForm({ ...brandForm, primary_color: e.target.value })}
                  data-testid="branding-color-input"
                  className="h-10 w-14 cursor-pointer rounded-md border border-slate-200 bg-white p-1" />
                <span className="font-mono text-sm text-slate-600">{brandForm.primary_color}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Portal path</Label>
              <div className="flex items-center gap-2">
                <Input value={brandForm.subdomain} onChange={(e) => setBrandForm({ ...brandForm, subdomain: e.target.value })}
                  placeholder="learndc" data-testid="branding-subdomain-input" className="max-w-[160px]" />
                <span className="font-mono text-sm text-slate-500">.tagverify.app</span>
              </div>
              <p className="text-xs text-slate-400">Clients access their white-labeled portal at /{brandForm.subdomain || "path"} (e.g. tagverify.app/{brandForm.subdomain || "path"})</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Preview</div>
              <div className="mt-2 flex items-center gap-2">
                {brandForm.logo_url ? (
                  <img src={brandForm.logo_url} alt="logo preview" className="h-9 w-9 rounded-lg object-contain" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg text-white" style={{ backgroundColor: brandForm.primary_color }}>
                    <Building2 size={16} />
                  </div>
                )}
                <div>
                  <div className="font-display font-bold text-slate-900">{brandForm.name}</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Powered by TAGVerify</div>
                </div>
                <span className="ml-auto rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: brandForm.primary_color }}>
                  Active nav
                </span>
              </div>
            </div>
            <Button onClick={saveBranding} className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="save-branding-button">Save branding</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={schoolOpen} onOpenChange={setSchoolOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">{isSuper ? "Add School / Client" : "Add School"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {isSuper && (
              <div className="grid grid-cols-2 gap-2">
                {[{ id: "existing", label: "Existing client" }, { id: "new", label: "New client" }].map((m) => (
                  <button key={m.id} type="button" onClick={() => setSchoolForm({ ...schoolForm, mode: m.id })}
                    data-testid={`school-mode-${m.id}`}
                    className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                      schoolForm.mode === m.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}>
                    {m.label}
                  </button>
                ))}
              </div>
            )}
            {isSuper && schoolForm.mode === "existing" && (
              <div className="space-y-1.5">
                <Label>Client</Label>
                <Select value={schoolForm.client_id} onValueChange={(v) => setSchoolForm({ ...schoolForm, client_id: v })}>
                  <SelectTrigger data-testid="school-client-select"><SelectValue placeholder="Select client">{clients.find((c) => c.id === schoolForm.client_id)?.name}</SelectValue></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {isSuper && schoolForm.mode === "new" && (
              <>
                <div className="space-y-1.5"><Label>Organization name</Label>
                  <Input value={schoolForm.new_name} onChange={(e) => setSchoolForm({ ...schoolForm, new_name: e.target.value })}
                    placeholder="e.g. Fairfax County Public Schools" data-testid="client-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Short code</Label>
                    <Input value={schoolForm.new_code} onChange={(e) => setSchoolForm({ ...schoolForm, new_code: e.target.value })}
                      placeholder="e.g. FCPS" data-testid="client-code-input" /></div>
                  <div className="space-y-1.5"><Label>Alert threshold (min)</Label>
                    <Input type="number" value={schoolForm.threshold}
                      onChange={(e) => setSchoolForm({ ...schoolForm, threshold: parseInt(e.target.value) || 60 })}
                      data-testid="client-threshold-input" /></div>
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>School name{isSuper && schoolForm.mode === "new" ? " (optional — add now or later)" : ""}</Label>
              <Input value={schoolForm.name} onChange={(e) => setSchoolForm({ ...schoolForm, name: e.target.value })}
                placeholder="e.g. Lincoln Elementary" data-testid="school-name-input" />
            </div>
            <Button onClick={saveSchool} className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="save-school-button">
              {isSuper && schoolForm.mode === "new" ? "Create client" : "Add school"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
